import GuideLink, { GuideKey, GuideRow } from 'components/GuideLink'
import { Box, Button, ButtonProps, Flex, Text } from '@pancakeswap/uikit'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { V3_ADDRESS } from '../config'
import { useContractBinding } from '../contract'
import { useV3Wallet } from '../hooks'
import { Display, MONO, surface } from '../ui'
import TickerBar from './TickerBar'

export const Page = styled(Box)`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 16px 96px;
  /* clip, not hidden: hidden makes the page a scroll container, and a position: sticky bar inside it (the list's filter bar) would never stick.
     Browsers without overflow: clip keep hidden (no sticky there, still no sideways scroll). */
  overflow-x: hidden;
  overflow-x: clip;
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 24px 24px 120px;
  }
`
export const Panel = styled(Box)`
  ${surface}
  padding: 16px;
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 24px;
  }
`
export const Mono = styled.span`
  font-family: ${MONO};
  font-size: 0.92em;
  overflow-wrap: anywhere;
  word-break: break-word;
`
const Tabs = styled(Flex)`
  gap: 8px;
  flex-wrap: wrap;
  margin: 0 0 20px;
`
const Tab = styled(Link)<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 15px;
  text-decoration: none;
  background: ${({ theme, $active }) => ($active ? theme.colors.tertiary : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.text : theme.colors.textSubtle)};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.cardBorder)};
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`

export function useV3Ready() {
  const { wrongChain, configured, address } = useV3Wallet()
  const { address: bound } = useContractBinding()
  return { ready: Boolean(bound || V3_ADDRESS) && !wrongChain, wrongChain, configured: configured || Boolean(bound), address }
}

/** The one place a wallet action starts: connect, switch to BNB Smart Chain, or the real action. */
export function ActionButton({ children, onClick, disabled, ...rest }: ButtonProps & { onClick?: () => void; 'data-testid'?: string }) {
  const { isConnected, wrongChain, switching, switchToBsc } = useV3Wallet()
  const big = { minHeight: 48, width: '100%' }
  const testId = rest['data-testid']
  if (!isConnected)
    return (
      <ConnectWalletButton style={big} data-testid={testId}>
        Connect wallet
      </ConnectWalletButton>
    )
  if (wrongChain)
    return (
      <Button style={big} onClick={switchToBsc} isLoading={switching} data-testid="v3-switch-chain">
        Switch to BNB Smart Chain
      </Button>
    )
  return (
    <Button style={big} onClick={onClick} disabled={disabled} {...rest}>
      {children}
    </Button>
  )
}

export function ChainNote() {
  const { configured, wrongChain, switching, switchToBsc } = useV3Wallet()
  if (configured && !wrongChain) return null
  return (
    <Panel mb="16px" data-testid="v3-chain-note">
      <Text color="warning" bold>
        {!configured
          ? 'The competition contract is not configured on this build.'
          : 'Your wallet is on another network. Trading Competition runs on BNB Smart Chain.'}
      </Text>
      <Text fontSize="14px" color="textSubtle">Results, leaderboards and reports stay readable.</Text>
      {configured && (
        <Button mt="12px" style={{ minHeight: 44 }} onClick={switchToBsc} isLoading={switching}>
          Switch to BNB Smart Chain
        </Button>
      )}
    </Panel>
  )
}

/** Page frame: section tabs, chain note, then the page. `title` is omitted on pages that open with their own hero. */
export default function Shell({ title, subtitle, guide, children }: { title?: string; subtitle?: string; /** a docs guide beside the title */ guide?: GuideKey; children: React.ReactNode }) {
  const { asPath } = useRouter()
  const current = asPath.split(/[?#]/)[0]
  const { tabs } = useContractBinding()
  return (
    <Page data-testid="v3-page">
      <TickerBar />
      <Tabs as="nav" aria-label="Trading Competition">
        {tabs.map((t) => (
          <Tab key={t.href} href={t.href} $active={current === t.href} aria-current={current === t.href ? 'page' : undefined}>
            {t.label}
          </Tab>
        ))}
      </Tabs>
      {title && (
        <Box mb="20px">
          {guide ? (
            <GuideRow>
              <Display as="h1" bold fontSize={['30px', null, '36px']} lineHeight="1.1">{title}</Display>
              <GuideLink guide={guide} />
            </GuideRow>
          ) : (
            <Display as="h1" bold fontSize={['30px', null, '36px']} lineHeight="1.1">{title}</Display>
          )}
          {subtitle && <Text color="textSubtle" mt="8px" style={{ maxWidth: 720 }}>{subtitle}</Text>}
        </Box>
      )}
      <ChainNote />
      {children}
    </Page>
  )
}
